import { useCallback } from 'react';

import { useSaveUserConfig, useUserConfig, useUserDetails } from '@/features/_shared/user';
import { toPersistedMessage } from '@/features/ai-chat/lib/persistedMessage';
import saveUserHistory, { deriveConversationName } from '@/features/ai-chat/lib/saveHistory';
import type { Conversation } from '@/features/ai-chat/types/chat.types';
import { clock } from '@/services/clock';

import type { UIMessage } from '@tanstack/ai-client';

export function useChatHistorySync(
  conversationId: string | null,
  messagesRef: React.RefObject<UIMessage[]>,
  ratings: Record<string, -1 | 0 | 1>
) {
  const { data: userConfig, isSuccess: isUserConfigLoaded } = useUserConfig();
  const { userEmail } = useUserDetails();
  const saveUserConfigMutation = useSaveUserConfig(userEmail ?? '');

  return useCallback(() => {
    if (!conversationId || !isUserConfigLoaded || !userConfig) return;
    const persistedMessages = (messagesRef.current ?? []).map(m =>
      toPersistedMessage(m, ratings[m.id])
    );
    const timestamp = clock.date().toISOString();
    const existing = userConfig.chatHistory as Conversation[];
    const idx = existing.findIndex(c => c.conversationId === conversationId);
    saveUserHistory({
      userConfig,
      saveUser: config => {
        saveUserConfigMutation.mutate(config);
      },
      updatedConversation: {
        conversationId,
        messages: persistedMessages,
        timestamp,
        name: deriveConversationName(persistedMessages)
      },
      existing,
      idx
    });
  }, [
    conversationId,
    saveUserConfigMutation,
    userConfig,
    isUserConfigLoaded,
    messagesRef,
    ratings
  ]);
}
